import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Star, Trash2, Pencil, Plus } from "lucide-react";
import { adminApi, formatErr } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const EMPTY = { author: "", rating: 5, when: "", text: "" };
const field = "w-full border border-paper-line bg-white px-4 py-3 text-sm outline-none focus:border-ink";

const ReviewForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: k === "rating" ? Number(e.target.value) : e.target.value }));
  return (
    <form data-testid="review-form" className="grid gap-3 border border-ink bg-white p-5 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); onSave(f); }}>
      <input required value={f.author} onChange={set("author")} placeholder="Nom du client (ex. Nicolas H.)" className={field} data-testid="review-author-input" />
      <div className="flex gap-3">
        <select value={f.rating} onChange={set("rating")} className={field} data-testid="review-rating-select">
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoiles</option>)}
        </select>
        <input value={f.when} onChange={set("when")} placeholder="Date (ex. juin 2024)" className={field} data-testid="review-when-input" />
      </div>
      <textarea required rows={4} value={f.text} onChange={set("text")} placeholder="Texte de l'avis tel qu'il apparaît sur Google" className={`${field} sm:col-span-2`} data-testid="review-text-input" />
      <div className="flex gap-2 sm:col-span-2">
        <button type="submit" className="bg-ink px-5 py-2.5 text-sm font-semibold text-paper hover:bg-gold hover:text-ink" data-testid="review-save-btn">Enregistrer</button>
        <button type="button" onClick={onCancel} className="border border-paper-line px-5 py-2.5 text-sm hover:border-ink" data-testid="review-cancel-btn">Annuler</button>
      </div>
    </form>
  );
};

const ReviewRow = ({ r, onEdit, onDelete }) => (
  <article data-testid={`admin-review-${r.id}`} className="flex flex-col gap-3 border border-paper-line bg-white p-5 sm:flex-row sm:items-start">
    <div className="flex-1">
      <div className="flex items-center gap-3">
        <span className="font-semibold">{r.author}</span>
        <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((k) => <Star key={k} className={`h-3.5 w-3.5 ${k <= r.rating ? "fill-gold text-gold" : "text-paper-line"}`} />)}</span>
        {r.when && <span className="text-xs text-slate-500">{r.when}</span>}
      </div>
      <p className="mt-2 text-sm text-slate-700">{r.text}</p>
    </div>
    <div className="flex gap-2">
      <button onClick={() => onEdit(r)} data-testid={`admin-review-edit-${r.id}`} className="flex h-9 w-9 items-center justify-center border border-paper-line hover:border-ink" aria-label="Modifier"><Pencil className="h-4 w-4" /></button>
      <button onClick={() => onDelete(r.id)} data-testid={`admin-review-delete-${r.id}`} className="flex h-9 w-9 items-center justify-center border border-paper-line text-red-700 hover:border-red-700" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>
    </div>
  </article>
);

export default function AdminReviews() {
  const nav = useNavigate();
  const [reviews, setReviews] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | review

  const fail = (e) => {
    if (e.response?.status === 401) { localStorage.removeItem("spd_token"); nav("/admin"); return; }
    toast.error(formatErr(e.response?.data?.detail));
  };
  useEffect(() => {
    adminApi().get("/admin/reviews").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(fail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (f) => {
    try {
      if (editing === "new") {
        const { data } = await adminApi().post("/admin/reviews", f);
        setReviews((l) => [data, ...l]);
      } else {
        const { data } = await adminApi().put(`/admin/reviews/${editing.id}`, f);
        setReviews((l) => l.map((x) => (x.id === data.id ? data : x)));
      }
      setEditing(null);
      toast.success("Avis enregistré — visible sur le site.");
    } catch (e) { fail(e); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try { await adminApi().delete(`/admin/reviews/${id}`); setReviews((l) => l.filter((x) => x.id !== id)); } catch (e) { fail(e); }
  };
  const logout = () => { localStorage.removeItem("spd_token"); nav("/admin"); };

  return (
    <main data-testid="admin-reviews-page" className="min-h-screen bg-paper">
      <header className="flex items-center justify-between border-b border-paper-line px-5 py-4 sm:px-8">
        <Link to="/"><Logo /></Link>
        <nav className="flex items-center gap-6 text-sm">
          <Link to="/admin/devis" data-testid="admin-nav-devis" className="link-line">Demandes de devis</Link>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2"><LogOut className="h-4 w-4" />Déconnexion</button>
        </nav>
      </header>
      <div className="mx-auto max-w-[960px] px-5 py-12 sm:px-8">
        <p className="eyebrow text-gold-deep mb-4">Avis Google</p>
        <h1 className="font-display text-5xl">Avis affichés sur le site</h1>
        <p className="mt-4 text-sm text-slate-600">Recopiez ici vos avis Google (nom, date, texte). Ils s'affichent immédiatement dans la section « Avis clients ». Dès que la connexion Google Places sera active, les avis seront synchronisés automatiquement.</p>
        <div className="mt-8">
          {editing ? (
            <ReviewForm initial={editing === "new" ? EMPTY : editing} onSave={onSave} onCancel={() => setEditing(null)} />
          ) : (
            <button onClick={() => setEditing("new")} data-testid="admin-review-add-btn" className="flex items-center gap-2 bg-ink px-5 py-3 text-sm font-semibold text-paper hover:bg-gold hover:text-ink"><Plus className="h-4 w-4" />Ajouter un avis</button>
          )}
        </div>
        <div className="mt-8 space-y-3">
          {reviews === null && <p className="text-slate-600">Chargement…</p>}
          {reviews && reviews.length === 0 && <p className="text-slate-600" data-testid="admin-reviews-empty">Aucun avis pour le moment.</p>}
          {(reviews || []).map((r) => <ReviewRow key={r.id} r={r} onEdit={setEditing} onDelete={onDelete} />)}
        </div>
      </div>
    </main>
  );
}
